import React, { useState, useEffect } from 'react';
import BBALoader from '../BBALoader';
import { useLocation } from 'react-router-dom';
import { FileText, Eye, Sparkles, Search } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCompanyId } from '../../hooks/useCompanyId';
import TailorTemplateModal from '../hr/TailorTemplateModal';
import DocumentViewerModal from '../DocumentViewerModal';
import { fetchTemplateStructure } from '../../services/hrStructureService';
import {
  fetchHRTemplates,
  hasTemplateFile,
  hasTailorHtml,
  findTemplateByTitle,
  getTemplateViewUrl,
  findTemplateDoc,
  findTailorableTemplate,
} from '../../utils/hrTemplates';

/**
 * CRM-managed materials for a department pillar, shown on the portal
 * department dashboards. Sections and templates are edited from the CRM
 * collections page for that pillar (templatesStructures/<structureId> +
 * templatesCollections docs with the matching pillar). Each card offers View
 * (client file), Tailor (token HTML), and — when the CRM saved a Canva link —
 * a Design with Canva button that opens it. Renders nothing until the
 * structure has at least one template (unless standalone).
 */
const MaterialCard = ({ item, documents, onTailor, onView }) => {
  const { darkMode } = useTheme();
  const available = hasTemplateFile(documents, item.title);
  const tailorable = hasTailorHtml(documents, item.title);
  const templateDoc = findTemplateDoc(documents, item.title);
  const canvaUrl = (templateDoc?.canvaUrl || '').trim();
  const version = String(templateDoc?.version || '').trim();

  const handleView = () => onView(item);

  const ghostBtn = `flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
    darkMode ? 'border-gray-700 text-gray-200 hover:bg-gray-700/60' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
  }`;
  const disabledBtn = `flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border ${
    darkMode ? 'border-gray-700 text-gray-600' : 'border-gray-200 text-gray-300'
  } cursor-not-allowed`;

  return (
    <div
      className={`rounded-xl border p-4 transition-shadow hover:shadow-md ${
        darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-center gap-3 mb-4">
        <span className="shrink-0 w-11 h-11 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center">
          {item.emoji ? (
            <span className="text-2xl leading-none">{item.emoji}</span>
          ) : (
            <FileText className="w-5 h-5" />
          )}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h4 className={`text-base md:text-lg font-bold leading-snug truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {item.name || item.title}
            </h4>
            {version && (
              <span
                className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${
                  darkMode ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-gray-100 text-gray-500 border border-gray-200'
                }`}
              >
                v{version}
              </span>
            )}
          </div>
          {item.description && (
            <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleView}
          disabled={!available}
          title={available ? 'View document' : 'File not available yet'}
          className={available ? ghostBtn : disabledBtn}
        >
          <Eye className="w-3.5 h-3.5" />
          View
        </button>
        {/* Same Tailor button whichever source exists: token HTML opens the
            tailor modal; a Canva-only template opens its Canva link instead.
            With neither, the button is omitted so View spans the full width. */}
        {(tailorable || canvaUrl) && (
          <button
            type="button"
            onClick={() => {
              if (tailorable) onTailor(item);
              else window.open(canvaUrl, '_blank', 'noopener,noreferrer');
            }}
            title={tailorable ? 'Tailor to your company' : 'Open this template in Canva'}
            className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Tailor
          </button>
        )}
      </div>
    </div>
  );
};

const materialDomId = (idPrefix, title) => `${idPrefix}-${String(title).replace(/[^a-zA-Z0-9]+/g, '-')}`;

const PillarMaterialsSection = ({
  standalone = false,
  pillar,
  structureId,
  idPrefix,
  emptyText,
}) => {
  const location = useLocation();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { companyId } = useCompanyId();

  const [sections, setSections] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [tailorItem, setTailorItem] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [highlightTitle, setHighlightTitle] = useState(null);

  // Deep link (?material=<title>) from What's New / search: scroll to the card.
  useEffect(() => {
    const material = new URLSearchParams(location.search).get('material');
    if (!material) return undefined;
    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(materialDomId(idPrefix, material));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightTitle(material);
      }
    }, 350);
    const clearTimer = setTimeout(() => setHighlightTitle(null), 3200);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
  }, [location.search, sections, idPrefix]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [structure, docs] = await Promise.all([
          fetchTemplateStructure(structureId, []),
          fetchHRTemplates(),
        ]);
        if (cancelled) return;
        setSections(structure);
        setDocuments(docs.filter((d) => (d.pillar || 'HR') === pillar));
      } catch (err) {
        console.error(`Error loading ${pillar} materials:`, err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [structureId, pillar]);

  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'users', currentUser.uid))
      .then((snap) => snap.exists() && setUserData(snap.data()))
      .catch((err) => console.error('Error loading user data for tailoring:', err));
  }, [currentUser]);

  // View opens the file in a fullscreen in-page overlay instead of a new tab.
  const handleView = (item) => {
    const url = getTemplateViewUrl(findTemplateByTitle(documents, item.title));
    if (!url) {
      alert('This template is not available yet. Please check back later.');
      return;
    }
    setViewerDoc({ url, title: item.name || item.title });
  };

  // Sections that actually have templates; search + group filter narrow them.
  const populatedSections = sections.filter((s) => (s.items || []).length > 0);
  const q = searchQuery.trim().toLowerCase();
  const visibleSections = populatedSections
    .filter((s) => groupFilter === 'all' || s.id === groupFilter)
    .map((s) => ({
      ...s,
      items: q
        ? s.items.filter(
            (i) =>
              (i.name || i.title).toLowerCase().includes(q) ||
              (i.description || '').toLowerCase().includes(q) ||
              s.label.toLowerCase().includes(q)
          )
        : s.items,
    }))
    .filter((s) => s.items.length > 0);

  if (loading) {
    if (!standalone) return null;
    return (
      <div className="flex justify-center py-16">
        <BBALoader size={40} />
      </div>
    );
  }

  if (populatedSections.length === 0) {
    if (!standalone) return null;
    return (
      <div
        className={`rounded-xl border p-8 text-center ${
          darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
        }`}
      >
        <FileText className={`w-8 h-8 mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
        <h3 className={`text-base font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          No materials yet
        </h3>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {emptyText}
        </p>
      </div>
    );
  }

  const inputBase = `rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
    darkMode
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
  }`;

  return (
    <div>
      {/* Search + template group filter */}
      {standalone && (
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search
              className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                darkMode ? 'text-gray-500' : 'text-gray-400'
              }`}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search materials..."
              className={`w-full pl-9 pr-3 py-2 ${inputBase}`}
            />
          </div>
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className={`sm:w-56 px-3 py-2 ${inputBase}`}
          >
            <option value="all">All groups</option>
            {populatedSections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.emoji ? `${s.emoji} ` : ''}{s.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {visibleSections.length === 0 && (
        <p className={`text-sm text-center py-10 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No materials match your search.
        </p>
      )}

      {visibleSections.map((section) => (
        <div key={section.id} className="mt-10 first:mt-0">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
              {section.emoji ? (
                <span className="text-xl leading-none">{section.emoji}</span>
              ) : (
                <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
              )}
            </span>
            <h2 className={`text-xl md:text-2xl font-bold tracking-tight leading-tight text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {section.label}
            </h2>
            <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {section.items.map((item) => (
              <div
                key={item.title}
                id={materialDomId(idPrefix, item.title)}
                className={`rounded-xl transition-shadow ${
                  highlightTitle === item.title ? 'ring-2 ring-emerald-500 shadow-lg' : ''
                }`}
              >
                <MaterialCard item={item} documents={documents} onTailor={setTailorItem} onView={handleView} />
              </div>
            ))}
          </div>
        </div>
      ))}

      <DocumentViewerModal
        isOpen={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        url={viewerDoc?.url}
        title={viewerDoc?.title}
      />

      <TailorTemplateModal
        isOpen={!!tailorItem}
        onClose={() => setTailorItem(null)}
        template={tailorItem}
        templateDoc={tailorItem ? findTailorableTemplate(documents, tailorItem.title) : null}
        companyId={companyId}
        userData={userData}
      />
    </div>
  );
};

export default PillarMaterialsSection;
